import ExcelJS from "exceljs";
import type { SalespersonCommission, CommissionStats } from "@/shared/types";
import { t } from "@/shared/lib/th-translations";
import { formatBangkokDate } from "@/shared/lib/commission-date-utils";

interface ExportExcelOptions {
  salespersons: SalespersonCommission[];
  stats?: CommissionStats;
  period?: {
    year?: number;
    month?: number;
    startDate?: Date | string;
    endDate?: Date | string;
  };
}

const HEADER_FILL: ExcelJS.FillPattern = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FF1F4E79" },
};

const HEADER_FONT: Partial<ExcelJS.Font> = {
  bold: true,
  color: { argb: "FFFFFFFF" },
  size: 11,
  name: "Tahoma",
};

const CURRENCY_FORMAT = '#,##0.00;[Red]-#,##0.00';
const NUMBER_FORMAT = '#,##0';

const BODY_FONT: Partial<ExcelJS.Font> = {
  size: 10,
  name: "Tahoma",
};

const TOTALS_FILL: ExcelJS.FillPattern = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFD6E4F0" },
};

const TOTALS_FONT: Partial<ExcelJS.Font> = {
  bold: true,
  size: 11,
  name: "Tahoma",
};

const THIN_BORDER: Partial<ExcelJS.Borders> = {
  top: { style: "thin", color: { argb: "FFD0D0D0" } },
  left: { style: "thin", color: { argb: "FFD0D0D0" } },
  bottom: { style: "thin", color: { argb: "FFD0D0D0" } },
  right: { style: "thin", color: { argb: "FFD0D0D0" } },
};

function formatPeriodLabel(period?: ExportExcelOptions["period"]): string {
  if (!period?.year || !period?.month) return "ไม่ระบุ";
  const monthNames = [
    "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน",
    "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม",
    "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
  ];
  return `${monthNames[period.month - 1]} ${period.year}`;
}

export async function exportCommissionExcel({
  salespersons,
  stats,
  period,
}: ExportExcelOptions): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Commission Dashboard";
  workbook.created = new Date();

  // --- Sheet 1: Commission Data ---
  const ws = workbook.addWorksheet("รายงานค่าคอมมิชชั่น", {
    views: [{ state: "frozen", ySplit: 4 }],
  });

  // Title row
  const titleRow = ws.addRow(["รายงานค่าคอมมิชชั่นพนักงานขาย"]);
  ws.mergeCells("A1:J1");
  titleRow.getCell(1).font = { bold: true, size: 16, name: "Tahoma", color: { argb: "FF1F4E79" } };
  titleRow.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
  titleRow.height = 30;

  // Period row
  const periodLabel = formatPeriodLabel(period);
  const periodRow = ws.addRow([`รอบระยะเวลา: ${periodLabel}`]);
  ws.mergeCells("A2:J2");
  periodRow.getCell(1).font = { size: 11, name: "Tahoma", color: { argb: "FF666666" } };
  periodRow.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
  periodRow.height = 22;

  // Empty spacer row
  ws.addRow([]);

  // Header row
  const headers = [
    t("dashboard.table.employee"),
    "ชื่อพนักงานขาย",
    t("dashboard.table.invoiceCount"),
    t("dashboard.table.totalSales"),
    t("dashboard.table.paidThisPeriod"),
    t("dashboard.table.commissionable"),
    t("dashboard.table.nonComm"),
    t("dashboard.table.pendingCollection"),
    t("dashboard.table.creditNotes"),
    t("dashboard.table.totalCommission"),
  ];

  const headerRow = ws.addRow(headers);
  headerRow.height = 28;
  headerRow.eachCell((cell) => {
    cell.font = HEADER_FONT;
    cell.fill = HEADER_FILL;
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = THIN_BORDER;
  });

  // Column widths
  ws.columns = [
    { width: 16 },  // employee number
    { width: 28 },  // name
    { width: 14 },  // invoice count
    { width: 18 },  // total sales
    { width: 18 },  // paid this period
    { width: 18 },  // commissionable
    { width: 18 },  // non-comm
    { width: 18 },  // pending collection
    { width: 18 },  // credit notes
    { width: 18 },  // total commission
  ];

  // Data rows
  const sortedData = [...salespersons].sort((a, b) => b.totalCommissionAmount - a.totalCommissionAmount);

  sortedData.forEach((sp, idx) => {
    const row = ws.addRow([
      sp.employeeNumber || sp.salespersonName,
      sp.salespersonName,
      sp.invoiceCount,
      sp.totalSales,
      sp.paidAmountInPeriod,
      sp.commissionableAmount || 0,
      sp.nonCommissionableAmount || 0,
      sp.pendingCollection || 0,
      sp.creditNotesVatInclusive || 0,
      sp.totalCommissionAmount,
    ]);

    const isEvenRow = idx % 2 === 0;
    row.eachCell((cell, colNumber) => {
      cell.font = BODY_FONT;
      cell.border = THIN_BORDER;

      if (isEvenRow) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFF5F8FC" },
        };
      }

      // Number formatting
      if (colNumber === 3) {
        cell.numFmt = NUMBER_FORMAT;
        cell.alignment = { horizontal: "center" };
      } else if (colNumber >= 4) {
        cell.numFmt = CURRENCY_FORMAT;
        cell.alignment = { horizontal: "right" };
      } else {
        cell.alignment = { vertical: "middle" };
      }
    });
  });

  // Totals row
  const totalsRowData = [
    "รวมทั้งหมด",
    "",
    salespersons.reduce((sum, sp) => sum + sp.invoiceCount, 0),
    salespersons.reduce((sum, sp) => sum + sp.totalSales, 0),
    salespersons.reduce((sum, sp) => sum + sp.paidAmountInPeriod, 0),
    salespersons.reduce((sum, sp) => sum + (sp.commissionableAmount || 0), 0),
    salespersons.reduce((sum, sp) => sum + (sp.nonCommissionableAmount || 0), 0),
    salespersons.reduce((sum, sp) => sum + (sp.pendingCollection || 0), 0),
    salespersons.reduce((sum, sp) => sum + (sp.creditNotesVatInclusive || 0), 0),
    salespersons.reduce((sum, sp) => sum + sp.totalCommissionAmount, 0),
  ];

  const totalsRow = ws.addRow(totalsRowData);
  totalsRow.height = 26;
  totalsRow.eachCell((cell, colNumber) => {
    cell.font = TOTALS_FONT;
    cell.fill = TOTALS_FILL;
    cell.border = {
      top: { style: "medium", color: { argb: "FF1F4E79" } },
      left: { style: "thin", color: { argb: "FFD0D0D0" } },
      bottom: { style: "medium", color: { argb: "FF1F4E79" } },
      right: { style: "thin", color: { argb: "FFD0D0D0" } },
    };

    if (colNumber === 3) {
      cell.numFmt = NUMBER_FORMAT;
      cell.alignment = { horizontal: "center" };
    } else if (colNumber >= 4) {
      cell.numFmt = CURRENCY_FORMAT;
      cell.alignment = { horizontal: "right" };
    }
  });

  // --- Sheet 2: Summary Stats ---
  if (stats) {
    const summaryWs = workbook.addWorksheet("สรุปภาพรวม");

    // Title
    const sTitleRow = summaryWs.addRow(["สรุปภาพรวมค่าคอมมิชชั่น"]);
    summaryWs.mergeCells("A1:B1");
    sTitleRow.getCell(1).font = { bold: true, size: 14, name: "Tahoma", color: { argb: "FF1F4E79" } };
    sTitleRow.height = 28;

    const sPeriodRow = summaryWs.addRow([`รอบระยะเวลา: ${formatPeriodLabel(period)}`]);
    summaryWs.mergeCells("A2:B2");
    sPeriodRow.getCell(1).font = { size: 11, name: "Tahoma", color: { argb: "FF666666" } };

    summaryWs.addRow([]);

    // Summary header
    const sHeaderRow = summaryWs.addRow(["รายการ", "ค่า"]);
    sHeaderRow.eachCell((cell) => {
      cell.font = HEADER_FONT;
      cell.fill = HEADER_FILL;
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = THIN_BORDER;
    });
    sHeaderRow.height = 26;

    const summaryItems: [string, number | string, string?][] = [
      ["จำนวนพนักงานขาย", stats.totalSalespersons, NUMBER_FORMAT],
      ["จำนวนใบแจ้งหนี้", stats.totalInvoices, NUMBER_FORMAT],
      ["ยอดขายรอบนี้ (บาท)", stats.totalSales, CURRENCY_FORMAT],
      ["ชำระในรอบนี้ (บาท)", stats.totalPaidInPeriod, CURRENCY_FORMAT],
      ["รอเก็บเงินรวม (บาท)", stats.totalPendingCollection || 0, CURRENCY_FORMAT],
      ["ค่าคอมมิชชั่นรวม (บาท)", stats.totalCommissionAmount, CURRENCY_FORMAT],
      ["อัตราค่าคอมมิชชั่นเฉลี่ย (%)", `${(stats.averageCommissionRate * 100).toFixed(2)}%`],
    ];

    if (stats.totalCreditNotesAmount) {
      summaryItems.push(["ใบลดหนี้รวม (บาท)", stats.totalCreditNotesAmount, CURRENCY_FORMAT]);
      summaryItems.push(["จำนวนใบลดหนี้", stats.totalCreditNotes || 0, NUMBER_FORMAT]);
    }

    summaryItems.forEach(([label, value, fmt], idx) => {
      const row = summaryWs.addRow([label, value]);
      row.eachCell((cell, colNumber) => {
        cell.font = { size: 10, name: "Tahoma" };
        cell.border = THIN_BORDER;

        if (idx % 2 === 0) {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFF5F8FC" },
          };
        }

        if (colNumber === 1) {
          cell.font = { size: 10, name: "Tahoma", bold: true };
        }
        if (colNumber === 2 && fmt) {
          cell.numFmt = fmt;
          cell.alignment = { horizontal: "right" };
        }
      });
    });

    summaryWs.getColumn(1).width = 32;
    summaryWs.getColumn(2).width = 24;
  }

  // Generate and download
  await downloadWorkbook(
    workbook,
    `commission-report-${period?.year || "current"}-${String(period?.month || 1).padStart(2, "0")}.xlsx`,
  );
}

// Shared workbook -> browser download helper
async function downloadWorkbook(workbook: ExcelJS.Workbook, filename: string): Promise<void> {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.style.display = "none";
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

// Apply standard header styling to a header row
function styleHeaderRow(row: ExcelJS.Row): void {
  row.height = 28;
  row.eachCell((cell) => {
    cell.font = HEADER_FONT;
    cell.fill = HEADER_FILL;
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = THIN_BORDER;
  });
}

interface SalespersonExportInvoice {
  taskNumber: string;
  customerName: string;
  status: string;
  preTaxAmount: number;
  paidAmount: number;
  paidDate: number | null;
  commissionAmount: number;
  isExcluded?: boolean;
}

interface SalespersonExportProductCommission {
  sku: string;
  category?: string;
  categoryName?: string;
  quantity: number;
  netAmount: number;
  commissionRate: number;
  commissionAmount: number;
}

interface SalespersonExportCreditNote {
  number: string;
  date: string;
  invoiceNumber: string | null;
  totalAmount: number;
  preTaxAmount: number;
  commissionImpact: number;
}

interface SalespersonExportOptions {
  salespersonName: string;
  employeeNumber?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  summary: {
    invoiceCount: number;
    totalSales: number;
    totalCommissionAmount: number;
  };
  invoices: SalespersonExportInvoice[];
  productCommissions: SalespersonExportProductCommission[];
  creditNotes: SalespersonExportCreditNote[];
}

/**
 * Export a single salesperson's commission detail (modal data) to a styled
 * multi-sheet xlsx: Invoices, Product Commissions, Credit Notes + summary header.
 * Admin-only — call site must gate on effective role.
 */
export async function exportSalespersonCommissionExcel({
  salespersonName,
  employeeNumber,
  startDate,
  endDate,
  summary,
  invoices,
  productCommissions,
  creditNotes,
}: SalespersonExportOptions): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Commission Dashboard";
  workbook.created = new Date();

  const periodLabel =
    startDate && endDate
      ? `${formatBangkokDate(new Date(startDate), "yyyy-MM-dd")} - ${formatBangkokDate(new Date(endDate), "yyyy-MM-dd")}`
      : "ไม่ระบุ";
  const title = `${t("invoiceModal.title")}: ${employeeNumber || salespersonName}`;

  // Writes the shared 3-row header (title, period, summary) to a worksheet.
  const writeSheetHeader = (ws: ExcelJS.Worksheet, mergeTo: string) => {
    const titleRow = ws.addRow([title]);
    ws.mergeCells(`A1:${mergeTo}1`);
    titleRow.getCell(1).font = { bold: true, size: 16, name: "Tahoma", color: { argb: "FF1F4E79" } };
    titleRow.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
    titleRow.height = 30;

    const periodRow = ws.addRow([`${t("invoiceModal.period")}: ${periodLabel}`]);
    ws.mergeCells(`A2:${mergeTo}2`);
    periodRow.getCell(1).font = { size: 11, name: "Tahoma", color: { argb: "FF666666" } };
    periodRow.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
    periodRow.height = 22;

    ws.addRow([]);
  };

  // --- Sheet 1: Invoices ---
  const invWs = workbook.addWorksheet(t("invoiceModal.tabs.invoices"), {
    views: [{ state: "frozen", ySplit: 4 }],
  });
  writeSheetHeader(invWs, "G");
  styleHeaderRow(
    invWs.addRow([
      t("invoiceModal.invoiceTable.taskNumber"),
      t("invoiceModal.invoiceTable.customerName"),
      t("invoiceModal.invoiceTable.status"),
      t("invoiceModal.invoiceTable.netAmount"),
      t("invoiceModal.invoiceTable.totalPaid"),
      t("invoiceModal.invoiceTable.paymentDate"),
      t("invoiceModal.invoiceTable.commissionAmount"),
    ]),
  );
  invWs.columns = [
    { width: 20 },
    { width: 32 },
    { width: 14 },
    { width: 16 },
    { width: 16 },
    { width: 16 },
    { width: 18 },
  ];
  invoices.forEach((inv, idx) => {
    const row = invWs.addRow([
      inv.taskNumber,
      inv.customerName,
      inv.isExcluded ? `${inv.status} (${t("invoiceModal.excluded")})` : inv.status,
      inv.preTaxAmount,
      inv.paidAmount,
      inv.paidDate ? formatBangkokDate(inv.paidDate, "yyyy-MM-dd") : "-",
      inv.isExcluded ? 0 : inv.commissionAmount,
    ]);
    const isEvenRow = idx % 2 === 0;
    row.eachCell((cell, colNumber) => {
      cell.font = BODY_FONT;
      cell.border = THIN_BORDER;
      if (isEvenRow) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF5F8FC" } };
      }
      if (colNumber === 4 || colNumber === 5 || colNumber === 7) {
        cell.numFmt = CURRENCY_FORMAT;
        cell.alignment = { horizontal: "right" };
      } else if (colNumber === 6) {
        cell.alignment = { horizontal: "right" };
      }
    });
  });

  // --- Sheet 2: Product Commissions ---
  const prodWs = workbook.addWorksheet(t("invoiceModal.tabs.products"), {
    views: [{ state: "frozen", ySplit: 4 }],
  });
  writeSheetHeader(prodWs, "F");
  styleHeaderRow(
    prodWs.addRow([
      t("invoiceModal.productTable.sku"),
      t("invoiceModal.productTable.category"),
      t("invoiceModal.productTable.quantity"),
      t("invoiceModal.productTable.netAmount"),
      t("invoiceModal.productTable.commissionRate"),
      t("invoiceModal.productTable.commissionAmount"),
    ]),
  );
  prodWs.columns = [
    { width: 18 },
    { width: 22 },
    { width: 14 },
    { width: 18 },
    { width: 16 },
    { width: 18 },
  ];
  productCommissions.forEach((p, idx) => {
    const row = prodWs.addRow([
      p.sku,
      p.categoryName || p.category || "",
      p.quantity,
      p.netAmount,
      p.commissionRate,
      p.commissionAmount,
    ]);
    const isEvenRow = idx % 2 === 0;
    row.eachCell((cell, colNumber) => {
      cell.font = BODY_FONT;
      cell.border = THIN_BORDER;
      if (isEvenRow) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF5F8FC" } };
      }
      if (colNumber === 3) {
        cell.numFmt = NUMBER_FORMAT;
        cell.alignment = { horizontal: "center" };
      } else if (colNumber === 5) {
        cell.numFmt = "0.0%";
        cell.alignment = { horizontal: "right" };
      } else if (colNumber === 4 || colNumber === 6) {
        cell.numFmt = CURRENCY_FORMAT;
        cell.alignment = { horizontal: "right" };
      }
    });
  });

  // --- Sheet 3: Credit Notes ---
  const cnWs = workbook.addWorksheet(t("invoiceModal.tabs.creditNotes"), {
    views: [{ state: "frozen", ySplit: 4 }],
  });
  writeSheetHeader(cnWs, "F");
  styleHeaderRow(
    cnWs.addRow([
      t("invoiceModal.creditNoteTable.number"),
      t("invoiceModal.creditNoteTable.date"),
      t("invoiceModal.creditNoteTable.invoiceNumber"),
      t("invoiceModal.creditNoteTable.vatInclusive"),
      t("invoiceModal.creditNoteTable.preTax"),
      t("invoiceModal.creditNoteTable.commissionImpact"),
    ]),
  );
  cnWs.columns = [
    { width: 20 },
    { width: 16 },
    { width: 20 },
    { width: 18 },
    { width: 18 },
    { width: 18 },
  ];
  creditNotes.forEach((cn, idx) => {
    const row = cnWs.addRow([
      cn.number,
      cn.date ? formatBangkokDate(new Date(cn.date), "yyyy-MM-dd") : "-",
      cn.invoiceNumber || "-",
      -Math.abs(cn.totalAmount),
      -Math.abs(cn.preTaxAmount),
      -Math.abs(cn.commissionImpact || 0),
    ]);
    const isEvenRow = idx % 2 === 0;
    row.eachCell((cell, colNumber) => {
      cell.font = BODY_FONT;
      cell.border = THIN_BORDER;
      if (isEvenRow) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF5F8FC" } };
      }
      if (colNumber >= 4) {
        cell.numFmt = CURRENCY_FORMAT;
        cell.alignment = { horizontal: "right" };
      }
    });
  });

  // Summary block appended to invoices sheet
  invWs.addRow([]);
  const summaryHeader = invWs.addRow([t("invoiceModal.summary.title")]);
  summaryHeader.getCell(1).font = TOTALS_FONT;
  const summaryRows: [string, number, string][] = [
    [t("invoiceModal.stats.totalInvoices"), summary.invoiceCount, NUMBER_FORMAT],
    [t("invoiceModal.stats.totalSales"), summary.totalSales, CURRENCY_FORMAT],
    [t("invoiceModal.stats.totalCommission"), summary.totalCommissionAmount, CURRENCY_FORMAT],
  ];
  summaryRows.forEach(([label, value, fmt]) => {
    const row = invWs.addRow([label, value]);
    row.getCell(1).font = TOTALS_FONT;
    row.getCell(1).fill = TOTALS_FILL;
    const valCell = row.getCell(2);
    valCell.numFmt = fmt;
    valCell.font = TOTALS_FONT;
    valCell.fill = TOTALS_FILL;
    valCell.alignment = { horizontal: "right" };
  });

  const fileSlug = (employeeNumber || salespersonName || "salesperson").replace(/[^\w-]/g, "_");
  const fileDate = endDate ? formatBangkokDate(new Date(endDate), "yyyy-MM-dd") : "current";
  await downloadWorkbook(workbook, `commission-${fileSlug}-${fileDate}.xlsx`);
}
